import { Component, Input, OnInit, OnDestroy, AfterViewInit, ElementRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { PATH_TRANSLATIONS, SupportedLang, SUPPORTED_LANGS } from '../shared/config/localized-paths';

@Component({
  selector: 'grades-menu',
  templateUrl: './grades-menu.component.html',
  styleUrls: ['./grades-menu.component.scss']
})
export class GradesMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  variant: Variant = Variant.FOOTER;

  menuItems = [
    { label: 'Overview', labelKey: 'gradesMenu.overview', ariaLabelKey: 'gradesMenu.ariaLabel.overview', route: '/overview', tabId: 'overview' },
    { label: 'K - 1', ariaLabelKey: 'gradesMenu.ariaLabel.gradesk1', route: '/gradesk-1', tabId: 'gradesk-1' },
    { label: '2 - 3', ariaLabelKey: 'gradesMenu.ariaLabel.grades23', route: '/grades2-3', tabId: 'grades2-3' },
    { label: '4 - 5', ariaLabelKey: 'gradesMenu.ariaLabel.grades45', route: '/grades4-5', tabId: 'grades4-5' },
    { label: '6', ariaLabelKey: 'gradesMenu.ariaLabel.grade6', route: '/grade6', tabId: 'grade6' },
    { label: '7 - 8', ariaLabelKey: 'gradesMenu.ariaLabel.grades78', route: '/grades7-8', tabId: 'grades7-8' },
    { label: '9 - 10', ariaLabelKey: 'gradesMenu.ariaLabel.grades910', route: '/grades9-10', tabId: 'grades9-10' },
    { label: '11 - 12', ariaLabelKey: 'gradesMenu.ariaLabel.grades1112', route: '/grades11-12', tabId: 'grades11-12' },
  ];

  activeTabId = '';

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.updateActiveTab();
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => this.updateActiveTab());

    // Force c11n-tabs to re-read labelText when language changes
    this.translateService.onLangChange.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.menuItems = this.menuItems.map(item => ({ ...item }));
      setTimeout(() => {
        this.applyAriaLabels();
        this.pinTabBoldWidths();
      });
    });
  }

  ngAfterViewInit(): void {
    this.applyAriaLabels();
    // Best-effort immediate pin so the fix applies even when document.fonts is unavailable.
    this.pinTabBoldWidths();
    // Re-pin after fonts load for accurate measurement with custom font metrics.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => this.pinTabBoldWidths());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabChange(tabId: string): void {
    const item = this.menuItems.find(i => i.tabId === tabId);
    if (item) {
      const lang = this.activeLang;
      const canonicalPath = item.route.replace(/^\//, '');
      const localizedPath = PATH_TRANSLATIONS[lang]?.[canonicalPath] ?? canonicalPath;
      this.router.navigate(['/', lang, localizedPath]);
    }
  }

  private get activeLang(): SupportedLang {
    const urlLang = this.router.url.split('/')[1];
    return (SUPPORTED_LANGS as readonly string[]).includes(urlLang)
      ? (urlLang as SupportedLang)
      : 'en';
  }

  private pinTabBoldWidths(): void {
    const host = this.elRef.nativeElement as HTMLElement;
    const tabs = host.querySelectorAll<HTMLElement>('.c11n-tabs__tab');
    tabs.forEach(tab => {
      const label = tab.querySelector<HTMLElement>('.c11n-tabs__tab-label');
      if (label) {
        // Temporarily force the label bold (with !important to override the existing
        // normal-weight rule) so we can measure the tab's full bold-state width.
        label.style.setProperty('font-weight', 'bold', 'important');
        const boldWidth = tab.getBoundingClientRect().width;
        label.style.removeProperty('font-weight');
        tab.style.minWidth = `${boldWidth}px`;
      }
    });
  }

  private applyAriaLabels(): void {
    const tabButtons = this.elRef.nativeElement.querySelectorAll('.c11n-tabs__tab');
    tabButtons.forEach((button: HTMLElement, index: number) => {
      const item = this.menuItems[index];
      if (item?.ariaLabelKey) {
        button.setAttribute('aria-label', this.translateService.instant(item.ariaLabelKey));
      }
    });
  }

  private updateActiveTab(): void {
    const lang = this.activeLang;
    const urlSegments = this.router.url.split('/');
    const matched = this.menuItems.find(item => {
      const canonicalKey = item.route.replace(/^\//, '');
      const localizedSegment = PATH_TRANSLATIONS[lang]?.[canonicalKey] ?? canonicalKey;
      return urlSegments.includes(localizedSegment);
    });
    this.activeTabId = matched ? matched.tabId : '';
  }
}

export enum Variant { HEADER = "header", HEADER_MINI = "header-mini", FOOTER = "footer" }